import React, { useEffect, useState, useCallback } from 'react';
import { recommendationsApi, CatalogRecommendationItem } from '../services/api';
import { Icon } from '../ds/components/Icon';
import { Button } from '../ds/components/Button';
import { ModalShell } from '../ds/ModalShell';
import { InputField } from '../ds/InputField';
import { ApartmentRecommendationItemsEditor } from './RecommendationDetails/ApartmentRecommendationItemsEditor';

/**
 * Zakładka „Zalecenia" — EDYTOR katalogu zaleceń budynkowych A/B/C/D (feedback właściciela
 * 2026-07-24). Jedna strona: wszystkie zalecenia w skrótach; klik w pozycję → edycja treści
 * (full_text) i nazwy (short_label). Zapis trafia do bazy (recommendation_items, migr. 034)
 * i od razu zasila checkboxy na tablecie kominiarzy oraz teksty wpisywane przez bota do CEEB.
 *
 * Blok C notatki (decyzja właściciela 2026-08-04): katalog jest też ROZSZERZALNY —
 * „Dodaj pozycję" / „Utwórz grupę" per rubryka, „Dodaj pod-pozycję" w grupie oraz
 * grupowanie/rozgrupowywanie istniejących pozycji. Kod nadaje serwer i jest STAŁY (jest
 * stemplowany w `inspections.recommendations[]` starych przeglądów), więc przenoszenie
 * między grupami go nie rusza. Usuwania NIE MA — decyzja właściciela jeszcze nie zapadła.
 */

// Tytuły kategorii = warstwa UI (spójne z mobilką).
const CATEGORY_TITLES: Record<string, string> = {
  A: '1 str. Prot. CEEB — Inne uchybienia mające pośredni wpływ na działanie przewodów kominowych',
  B: '1 str. Prot. CEEB — rubryka: INNE UWAGI',
  C: '1 str. Prot. CEEB — rubryka: NIEPRAWIDŁOWOŚCI',
  D: '3 str. Prot. CEEB — UWAGI',
};

interface EditState {
  code: string;
  shortLabel: string;
  fullText: string;
}

/** Co zakładamy: zwykłą pozycję rubryki, pod-pozycję istniejącej grupy albo nową grupę. */
interface CreateState {
  category: string;
  isGroup: boolean;
  parentCode: string | null;
  parentLabel: string | null;
  shortLabel: string;
  fullText: string;
  /** Tylko dla grupy — jej pierwsza pod-pozycja. Grupa nie może powstać pusta: na tablecie
   *  byłaby zwykłym, zaznaczalnym checkboxem, którego bot nie wpisze do protokołu CEEB. */
  firstLabel: string;
  firstText: string;
}

interface MoveState {
  code: string;
  label: string;
  groups: CatalogRecommendationItem[];
  parentCode: string;
}

const rowActionStyle: React.CSSProperties = {
  minHeight: 'var(--touch-min)',
  padding: '0 var(--space-3)',
  flexShrink: 0,
};

const RecommendationsCatalogTab: React.FC = () => {
  const [categories, setCategories] = useState<{ id: string; items: CatalogRecommendationItem[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedCode, setSavedCode] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [creating, setCreating] = useState<CreateState | null>(null);
  const [moving, setMoving] = useState<MoveState | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await recommendationsApi.getCatalog();
      setCategories(data.categories);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się pobrać katalogu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-dismiss toastu (wzorzec ClientsTab).
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const startEdit = (item: CatalogRecommendationItem) => {
    setSavedCode(null);
    setEditing({ code: item.code, shortLabel: item.shortLabel, fullText: item.fullText });
  };

  const handleSave = async () => {
    if (!editing || saving) return;
    setSaving(true);
    try {
      await recommendationsApi.updateItem(editing.code, {
        shortLabel: editing.shortLabel,
        fullText: editing.fullText,
      });
      setSavedCode(editing.code);
      setEditing(null);
      await load();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Nie udało się zapisać zalecenia', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Dodawanie ──────────────────────────────────────────────────────────────
  const openCreate = (category: string, opts: { isGroup?: boolean; parent?: CatalogRecommendationItem } = {}) => {
    setModalError(null);
    setCreating({
      category,
      isGroup: opts.isGroup === true,
      parentCode: opts.parent ? opts.parent.code : null,
      parentLabel: opts.parent ? opts.parent.shortLabel : null,
      shortLabel: '',
      fullText: '',
      firstLabel: '',
      firstText: '',
    });
  };

  const handleCreate = async () => {
    if (!creating || busy) return;
    if (!creating.shortLabel.trim()) {
      setModalError(creating.isGroup ? 'Podaj nazwę grupy' : 'Podaj nazwę widoczną na liście');
      return;
    }
    if (!creating.isGroup && !creating.fullText.trim()) {
      setModalError('Podaj pełną treść — to ona trafia do protokołu CEEB');
      return;
    }
    if (creating.isGroup && !creating.firstLabel.trim()) {
      setModalError('Podaj nazwę pierwszej pozycji w grupie');
      return;
    }
    if (creating.isGroup && !creating.firstText.trim()) {
      setModalError('Podaj pełną treść pierwszej pozycji — to ona trafia do protokołu CEEB');
      return;
    }
    setBusy(true);
    setModalError(null);
    try {
      const created = await recommendationsApi.createItem({
        category: creating.category,
        shortLabel: creating.shortLabel.trim(),
        fullText: creating.isGroup ? undefined : creating.fullText,
        parentCode: creating.parentCode,
        isGroup: creating.isGroup || undefined,
        firstItem: creating.isGroup
          ? { shortLabel: creating.firstLabel.trim(), fullText: creating.firstText }
          : undefined,
      });
      setCreating(null);
      setToast({
        msg: creating.isGroup
          ? `Utworzono grupę ${created.code} z pozycją ${created.subItems?.[0]?.code ?? ''}`.trim()
          : `Dodano pozycję ${created.code}`,
        severity: 'success',
      });
      await load();
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Nie udało się dodać pozycji');
    } finally {
      setBusy(false);
    }
  };

  // ── Grupowanie ─────────────────────────────────────────────────────────────
  const openMove = (item: CatalogRecommendationItem, groups: CatalogRecommendationItem[]) => {
    setModalError(null);
    setMoving({ code: item.code, label: item.shortLabel, groups, parentCode: groups[0]?.code ?? '' });
  };

  const applyMove = async (code: string, parentCode: string | null, successMsg: string) => {
    setBusy(true);
    setModalError(null);
    try {
      await recommendationsApi.moveToGroup(code, parentCode);
      setMoving(null);
      setToast({ msg: successMsg, severity: 'success' });
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Nie udało się zmienić grupy';
      if (parentCode === null) setToast({ msg, severity: 'error' });
      else setModalError(msg);
    } finally {
      setBusy(false);
    }
  };

  const renderEditor = () => {
    if (!editing) return null;
    return (
      <div
        style={{
          border: 'var(--border-width-strong) solid var(--action-primary)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-4)',
          margin: 'var(--space-2) 0 var(--space-4)',
          background: 'var(--surface-fill)',
        }}
      >
        <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
          Edycja zalecenia <strong>{editing.code}</strong>
        </div>
        <label style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 600, marginBottom: 4 }}>
          Nazwa (skrót na liście)
        </label>
        <input
          type="text"
          value={editing.shortLabel}
          onChange={(e) => setEditing({ ...editing, shortLabel: e.target.value })}
          style={{
            width: '100%',
            padding: 'var(--space-2) var(--space-3)',
            border: 'var(--border-width) solid var(--border-strong)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
            marginBottom: 'var(--space-3)',
          }}
        />
        <label style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 600, marginBottom: 4 }}>
          Pełna treść (trafia do protokołu CEEB)
        </label>
        <textarea
          value={editing.fullText}
          onChange={(e) => setEditing({ ...editing, fullText: e.target.value })}
          rows={6}
          style={{
            width: '100%',
            padding: 'var(--space-2) var(--space-3)',
            border: 'var(--border-width) solid var(--border-strong)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
            lineHeight: 1.5,
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', marginTop: 'var(--space-3)' }}>
          <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
            Anuluj
          </Button>
          <Button variant="primary" icon="check" onClick={handleSave} disabled={saving}>
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </Button>
        </div>
      </div>
    );
  };

  const renderItemRow = (
    item: CatalogRecommendationItem,
    ctx: {
      nested: boolean;
      category: string;
      groups: CatalogRecommendationItem[];
      /** Ile pod-pozycji ma grupa-rodzic — ostatniej nie wolno wyjąć (grupa nie może opustoszeć). */
      siblings?: number;
    },
  ) => {
    const isOnlyChild = ctx.nested && ctx.siblings === 1;
    const isEditing = editing?.code === item.code;
    const justSaved = savedCode === item.code;
    return (
      <div key={item.code}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            borderTop: 'var(--border-width) solid var(--border)',
            background: isEditing ? 'var(--surface-hover)' : 'transparent',
            paddingRight: 'var(--space-3)',
          }}
        >
          <button
            type="button"
            onClick={() => startEdit(item)}
            title={`Edytuj treść (${item.code})`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
              padding: 'var(--space-3) var(--space-4)',
              paddingLeft: ctx.nested ? 'var(--space-8)' : 'var(--space-4)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <Icon name="pencil" size={16} color="var(--text-muted)" />
            <span style={{ flex: 1, fontSize: 'var(--text-body)', color: 'var(--text-primary)' }}>
              {item.shortLabel}
              {item.isGroup && (
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}> — grupa</span>
              )}
            </span>
            <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-muted)' }}>{item.code}</span>
            {justSaved && (
              <span style={{ fontSize: 'var(--text-label)', color: 'var(--status-ok, #2e7d32)', fontWeight: 600 }}>
                Zapisano ✓
              </span>
            )}
          </button>

          {item.isGroup ? (
            <Button
              variant="ghost"
              icon="plus"
              onClick={() => openCreate(ctx.category, { parent: item })}
              aria-label={`Dodaj pod-pozycję do grupy ${item.shortLabel}`}
              style={rowActionStyle}
            >
              Dodaj pod-pozycję
            </Button>
          ) : ctx.nested ? (
            <Button
              variant="ghost"
              icon="chevron-up"
              onClick={() => applyMove(item.code, null, `Pozycja ${item.code} wyjęta z grupy`)}
              disabled={busy || isOnlyChild}
              aria-label={`Wyciągnij pozycję ${item.code} z grupy`}
              title={isOnlyChild
                ? 'To ostatnia pozycja w grupie — grupa nie może zostać pusta. Najpierw przenieś tu inną pozycję.'
                : undefined}
              style={rowActionStyle}
            >
              Wyciągnij z grupy
            </Button>
          ) : ctx.groups.length > 0 ? (
            <Button
              variant="ghost"
              icon="chevron-down"
              onClick={() => openMove(item, ctx.groups)}
              disabled={busy}
              aria-label={`Przenieś pozycję ${item.code} do grupy`}
              style={rowActionStyle}
            >
              Przenieś do grupy
            </Button>
          ) : null}
        </div>
        {isEditing && renderEditor()}
        {item.subItems?.map((sub) => renderItemRow(sub, {
          ...ctx, nested: true, siblings: item.subItems!.length,
        }))}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
        <Icon name="clipboard" size={26} color="var(--action-primary)" />
        <h2 style={{ fontSize: 'var(--text-h2, 22px)', fontWeight: 700, color: 'var(--text-primary)' }}>
          Zalecenia — edycja treści
        </h2>
      </div>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-5)', maxWidth: 760 }}>
        Kliknij zalecenie, aby edytować jego pełną treść (to ona trafia do protokołu CEEB) lub nazwę
        widoczną na liście. Możesz też dodawać nowe pozycje i układać je w grupy. Zmiany od razu
        obowiązują na tablecie kominiarzy i w bocie CEEB. Kod pozycji (np. C24) nadaje system i już
        się nie zmienia — także po przeniesieniu do innej grupy.
      </p>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Ładowanie katalogu…</p>}
      {error && (
        <p style={{ color: 'var(--status-error, #c62828)' }}>
          {error} — <button type="button" onClick={load} style={{ textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', color: 'inherit' }}>spróbuj ponownie</button>
        </p>
      )}

      {!loading && !error && categories.map((cat) => {
        const groups = cat.items.filter((i) => i.isGroup);
        return (
          <section
            key={cat.id}
            style={{
              border: 'var(--border-width) solid var(--border)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-5)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-3) var(--space-3) var(--space-4)',
                background: 'var(--surface-fill)',
                fontWeight: 600,
                fontSize: 'var(--text-body)',
                color: 'var(--text-primary)',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>{CATEGORY_TITLES[cat.id] || `Kategoria ${cat.id}`}</span>
              <Button
                variant="ghost"
                icon="plus"
                onClick={() => openCreate(cat.id)}
                aria-label={`Dodaj pozycję w rubryce ${cat.id}`}
                style={rowActionStyle}
              >
                Dodaj pozycję
              </Button>
              <Button
                variant="ghost"
                icon="clipboard"
                onClick={() => openCreate(cat.id, { isGroup: true })}
                aria-label={`Utwórz grupę w rubryce ${cat.id}`}
                style={rowActionStyle}
              >
                Utwórz grupę
              </Button>
            </div>
            {cat.items.map((item) => renderItemRow(item, { nested: false, category: cat.id, groups }))}
          </section>
        );
      })}

      {/* Zalecenia dla LOKALI (migr. 059) + ich listy „Typ urządzenia" (migr. 054) — osobno od A/B/C/D */}
      <ApartmentRecommendationItemsEditor />

      {/* Dodawanie pozycji / grupy / pod-pozycji */}
      <ModalShell
        open={creating !== null}
        title={
          creating?.isGroup
            ? `Nowa grupa — rubryka ${creating?.category}`
            : creating?.parentCode
              ? `Nowa pod-pozycja w grupie ${creating?.parentLabel}`
              : `Nowa pozycja — rubryka ${creating?.category}`
        }
        onClose={() => { if (!busy) setCreating(null); }}
        width={620}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(null)} disabled={busy}>Anuluj</Button>
            <Button variant="primary" icon="plus" onClick={handleCreate} disabled={busy}>
              {busy ? 'Dodawanie…' : (creating?.isGroup ? 'Utwórz grupę' : 'Dodaj pozycję')}
            </Button>
          </>
        }
      >
        {creating && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {modalError && (
              <div
                role="alert"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--status-deny-bg)',
                  border: 'var(--border-width) solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--status-deny)',
                  fontSize: 'var(--text-label)',
                }}
              >
                <Icon name="triangle-alert" size={18} color="var(--status-deny)" />
                <span>{modalError}</span>
              </div>
            )}
            <InputField
              label={creating.isGroup ? 'Nazwa grupy (nagłówek na liście)' : 'Nazwa (skrót na liście)'}
              value={creating.shortLabel}
              onChange={(e) => setCreating({ ...creating, shortLabel: e.target.value })}
              hint={creating.isGroup
                ? 'Grupa to sam nagłówek — na tablecie nie da się jej zaznaczyć, zaznacza się jej pod-pozycje.'
                : 'To widzi kominiarz przy checkboxie na tablecie.'}
              autoFocus
              disabled={busy}
            />
            {!creating.isGroup && (
              <InputField
                label="Pełna treść (trafia do protokołu CEEB)"
                multiline
                rows={7}
                value={creating.fullText}
                onChange={(e) => setCreating({ ...creating, fullText: e.target.value })}
                disabled={busy}
              />
            )}
            {creating.isGroup && (
              <>
                <div
                  style={{
                    padding: 'var(--space-3) var(--space-4)',
                    background: 'var(--surface-fill)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-secondary)',
                    fontSize: 'var(--text-label)',
                  }}
                >
                  Grupa nie może być pusta — pusta pokazałaby się kominiarzowi jako zwykły checkbox,
                  którego treść nie trafiłaby do protokołu CEEB. Podaj jej pierwszą pozycję; kolejne
                  dołożysz przyciskiem „Dodaj pod-pozycję".
                </div>
                <InputField
                  label="Nazwa pierwszej pozycji w grupie"
                  value={creating.firstLabel}
                  onChange={(e) => setCreating({ ...creating, firstLabel: e.target.value })}
                  disabled={busy}
                />
                <InputField
                  label="Pełna treść pierwszej pozycji (trafia do protokołu CEEB)"
                  multiline
                  rows={6}
                  value={creating.firstText}
                  onChange={(e) => setCreating({ ...creating, firstText: e.target.value })}
                  disabled={busy}
                />
              </>
            )}
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
              Kod {creating.isGroup ? 'grupy i pozycji nada' : 'nada'} system (kolejny wolny
              w rubryce {creating.category}) i już go nie zmieni.
            </p>
          </div>
        )}
      </ModalShell>

      {/* Przeniesienie pozycji do grupy */}
      <ModalShell
        open={moving !== null}
        title="Przenieś do grupy"
        onClose={() => { if (!busy) setMoving(null); }}
        width={520}
        footer={
          <>
            <Button variant="secondary" onClick={() => setMoving(null)} disabled={busy}>Anuluj</Button>
            <Button
              variant="primary"
              icon="check"
              disabled={busy || !moving?.parentCode}
              onClick={() => moving && applyMove(
                moving.code, moving.parentCode, `Pozycja ${moving.code} przeniesiona do grupy ${moving.parentCode}`)}
            >
              {busy ? 'Przenoszenie…' : 'Przenieś'}
            </Button>
          </>
        }
      >
        {moving && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {modalError && (
              <div
                role="alert"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--status-deny-bg)',
                  border: 'var(--border-width) solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--status-deny)',
                  fontSize: 'var(--text-label)',
                }}
              >
                <Icon name="triangle-alert" size={18} color="var(--status-deny)" />
                <span>{modalError}</span>
              </div>
            )}
            <p style={{ margin: 0 }}>
              Pozycja <strong>{moving.label}</strong> ({moving.code}) trafi na koniec wybranej grupy.
              Kod pozycji zostaje bez zmian.
            </p>
            {/* Natywny select — brak DS Select (wzorzec AddBuildingModal). */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label
                htmlFor="catalog-move-group"
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-label)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--text-secondary)',
                }}
              >
                Grupa docelowa
              </label>
              <select
                id="catalog-move-group"
                value={moving.parentCode}
                disabled={busy}
                onChange={(e) => setMoving({ ...moving, parentCode: e.target.value })}
                style={{
                  width: '100%',
                  minHeight: 'var(--touch-min)',
                  padding: '0 var(--space-4)',
                  boxSizing: 'border-box',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-body)',
                  color: 'var(--text-primary)',
                  background: 'var(--surface-card)',
                  border: 'var(--border-width-strong) solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                {moving.groups.map((g) => (
                  <option key={g.code} value={g.code}>{g.shortLabel} ({g.code})</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </ModalShell>

      {/* Toast (DS) — wzorzec ClientsTab */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--space-6)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            maxWidth: 'min(92vw, 560px)',
            padding: 'var(--space-3) var(--space-4)',
            background: toast.severity === 'success' ? 'var(--status-ok-bg)' : 'var(--status-deny-bg)',
            border: 'var(--border-width) solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-float)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-label)',
            color: toast.severity === 'success' ? 'var(--status-ok)' : 'var(--status-deny)',
          }}
        >
          <Icon
            name={toast.severity === 'success' ? 'check' : 'triangle-alert'}
            size={18}
            color={toast.severity === 'success' ? 'var(--status-ok)' : 'var(--status-deny)'}
          />
          <span style={{ flex: 1 }}>{toast.msg}</span>
        </div>
      )}
    </div>
  );
};

export default RecommendationsCatalogTab;
